import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import ItineraryItemCard from './ItineraryItemCard';
import PlatformIcon from '../common/PlatformIcon';
import AirlineLogo from '../common/AirlineLogo';
import { ORANGE_ACTION } from '../../constants/colors';
import { getAirportCity, getAirportCountry, getAirlineIata } from '../../constants/iataAirports';

export interface TransportCardProps {
  title: string;
  note?: string;
  mode?: 'flight' | 'train' | 'bus' | 'metro' | 'car' | 'ship' | 'ferry' | 'cruise' | 'walk' | 'taxi' | 'bike' | 'other';
  from?: string;
  to?: string;
  fromLabel?: string;
  toLabel?: string;
  fromDateLabel?: string;
  toDateLabel?: string;
  code?: string;
  duration?: string;
  startTime?: string;
  endTime?: string;
  operator?: string; // airline / carrier name or IATA code
}

const modeIcon: Record<string, string> = {
  flight: 'flight-takeoff',
  train: 'train',
  bus: 'directions_bus',
  metro: 'directions_subway',
  car: 'directions_car',
  ship: 'directions_boat',
  ferry: 'directions_boat',
  cruise: 'directions_boat',
  walk: 'directions_walk',
  taxi: 'local_taxi',
  bike: 'directions_bike',
  other: 'travel_explore',
};

/**
 * Calculate flight duration from startTime and endTime
 * Returns formatted string like "3h 25m" or "3小时25分钟"
 */
const calculateDuration = (startTime?: string, endTime?: string): string | undefined => {
  if (!startTime || !endTime) return undefined;
  
  try {
    // Parse time strings - handle multiple formats
    // Formats: "HH:MM:SS", "HH:MM", "HH", or ISO 8601 times
    const parseTime = (timeStr: string): { hours: number; minutes: number } | undefined => {
      // Remove any timezone info
      timeStr = timeStr.split('+')[0].split('Z')[0];
      const parts = timeStr.split(':');
      if (parts.length < 2) return undefined;
      
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      
      if (isNaN(hours) || isNaN(minutes)) return undefined;
      return { hours, minutes };
    };
    
    const start = parseTime(startTime);
    const end = parseTime(endTime);
    
    if (!start || !end) return undefined;
    
    // Calculate difference in minutes
    let totalMinutes = (end.hours * 60 + end.minutes) - (start.hours * 60 + start.minutes);
    
    // Handle case where end time is next day
    if (totalMinutes < 0) {
      totalMinutes += 24 * 60;
    }
    
    if (totalMinutes === 0) return undefined;
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    // Format: "3h 25m" or just hours/minutes if one is 0
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  } catch (e) {
    return undefined;
  }
};

/**
 * Format time string from HH:MM:SS to HH:MM
 */
const formatTime = (timeStr?: string): string | undefined => {
  if (!timeStr) return undefined;
  
  try {
    // Remove any timezone or date info
    let cleanTime = timeStr.split('+')[0].split('Z')[0];
    
    // Handle ISO 8601 datetime format (remove date part)
    if (cleanTime.includes('T')) {
      cleanTime = cleanTime.split('T')[1];
    }
    
    const parts = cleanTime.split(':');
    if (parts.length >= 2) {
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1].padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    return timeStr;
  } catch (e) {
    return timeStr;
  }
};

/**
 * Format date from date string (YYYY-MM-DD or similar)
 * Returns formatted string like "Sun, Nov 3"
 */
const formatDate = (dateStr?: string): string | undefined => {
  if (!dateStr) return undefined;
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return undefined;
    
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
  } catch (e) {
    return undefined;
  }
};

const TransportCard: React.FC<TransportCardProps> = (props) => {
  const {
    mode,
    from,
    to,
    fromLabel,
    toLabel,
    fromDateLabel,
    toDateLabel,
    duration,
    code,
    startTime,
    endTime,
    note,
  } = props;

  const iconName = mode ? (modeIcon[mode] || 'travel-explore') : 'travel-explore';
  
  // Derive display values from IATA codes if labels not provided
  const isAirMode = mode === 'flight';

  const deriveLocations = (code?: string, fallback?: string): string | undefined => {
    if (!code) return fallback;
    if (!isAirMode) return fallback;
    const mapped = getAirportCity(code);
    if (!mapped || mapped === code) return fallback || code;
    return mapped;
  };

  const displayFromCity = deriveLocations(from, fromLabel || from);
  const displayToCity = deriveLocations(to, toLabel || to);
  
  // Format times and calculate duration
  const formattedStartTime = formatTime(startTime);
  const formattedEndTime = formatTime(endTime);
  const calculatedDuration = duration || calculateDuration(startTime, endTime);

  // If operator present, try to resolve airline IATA code (support Chinese and other aliases)
  // We allow resolving operator for any transport mode so carrier/operator input is visible on cards
  // Resolve airline IATA from operator when available; if not, try to infer from the code (e.g. "CX 789").
  let airlineIata: string | undefined;
  if (isAirMode && props.operator) {
    airlineIata = getAirlineIata(props.operator);
  }
  if (!airlineIata && props.code) {
    const codeStr = String(props.code).trim();
    // Prefer start-of-string airline code like "CX189" or "CX 189"
    let m = codeStr.match(/^([A-Za-z]{2,3})\b/);
    // Fallback: find the first 2-3 letter group anywhere (covers inputs like "LX CX 789")
    if (!m) m = codeStr.match(/([A-Za-z]{2,3})/);
    if (m && m[1]) {
      const candidate = m[1].toUpperCase();
      airlineIata = isAirMode ? candidate : undefined;
    }
  }
  const airlineDisplay = isAirMode ? (airlineIata || props.operator || undefined) : undefined;
  const rawCode = code ? String(code).trim() : undefined;
  // Build the center text carefully to avoid duplicating the IATA prefix.
  let centerText: string | undefined;
  if (airlineDisplay) {
    if (rawCode) {
      // If the code already contains the IATA (e.g. "CX189" or "CX 189"), don't prefix it again.
      const normalizedCode = rawCode.replace(/\s+/g, '').toUpperCase();
      const iataNorm = airlineIata ? airlineIata.replace(/\s+/g, '').toUpperCase() : undefined;
      const codeHasIata = iataNorm ? normalizedCode.startsWith(iataNorm) : false;
      if (codeHasIata) {
        centerText = rawCode;
      } else {
        centerText = `${airlineDisplay}${rawCode ? ` ${rawCode}` : ''}`;
      }
    } else {
      centerText = airlineDisplay;
    }
  } else {
    centerText = rawCode || undefined;
  }

  // Format dates - use provided labels, or extract from startTime if available
  const displayFromDate = fromDateLabel;
  const displayToDate = toDateLabel;

  // Debug logging
  if (from === 'PVG' || to === 'MFM') {
    console.log('TransportCard DEBUG:', {
      from, to, code, mode,
      startTime, endTime, duration,
      formattedStartTime, formattedEndTime, calculatedDuration,
      fromDateLabel, toDateLabel,
      displayFromDate, displayToDate,
    });
  }

  return (
    <ItineraryItemCard>
      <View style={styles.transportRow}>
        <View style={styles.placeCol}>
          <Text style={styles.placeCode}>{from || '--'}</Text>
          {displayFromCity ? <Text style={styles.placeLabel} numberOfLines={1}>{displayFromCity}</Text> : null}
          {displayFromDate ? <Text style={styles.placeDate} numberOfLines={1}>{displayFromDate}</Text> : null}
          {formattedStartTime ? <Text style={styles.placeTime}>{formattedStartTime}</Text> : null}
        </View>

        <View style={styles.centerCol}>
          {/* Flight code above connector */}
          {code || airlineDisplay ? (
            <View style={{ alignItems: 'center' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                {airlineDisplay ? (
                  <AirlineLogo iata={airlineIata} operator={props.operator} size={18} style={{ marginRight: 6 }} />
                ) : null}
                <Text style={styles.centerCode}>{centerText}</Text>
              </View>
            </View>
          ) : null}
          
          {/* Connector row: left dashed line - plane icon - right dashed line */}
          {/* All aligned at the vertical center where placeCode sits */}
          <View style={styles.connectorRow}>
            {/* Left dashed line - extends almost to left IATA code */}
            <View style={styles.dashedLine} />
            
            {/* Transport mode icon centered */}
            <View style={styles.planeWrapper}>
              <PlatformIcon 
                name={iconName} 
                size={20} 
                color={ORANGE_ACTION} 
                family="MaterialIcons"
              />
            </View>
            
            {/* Right dashed line - extends almost to right IATA code */}
            <View style={styles.dashedLine} />
          </View>

          {/* Duration below connector */}
          {calculatedDuration ? <Text style={styles.centerDuration}>{calculatedDuration}</Text> : null}
        </View>

        <View style={[styles.placeCol, styles.placeColRight]}>
          <Text style={[styles.placeCode, styles.placeCodeRight]}>{to || '--'}</Text>
          {displayToCity ? <Text style={[styles.placeLabel, styles.placeLabelRight]} numberOfLines={1}>{displayToCity}</Text> : null}
          {displayToDate ? <Text style={[styles.placeDate, styles.placeDateRight]} numberOfLines={1}>{displayToDate}</Text> : null}
          {formattedEndTime ? <Text style={[styles.placeTime, styles.placeTimeRight]}>{formattedEndTime}</Text> : null}
        </View>
      </View>
      {note ? <Text style={styles.note}>{note}</Text> : null}
    </ItineraryItemCard>
  );
};

const styles = StyleSheet.create({
  transportRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' },
  placeCol: { width: 80, justifyContent: 'flex-start', paddingTop: 0 },
  placeColRight: { alignItems: 'flex-end' },
  placeCode: { fontSize: 20, fontWeight: '700', color: '#0F172A', lineHeight: 28 },
  placeCodeRight: { textAlign: 'right' },
  placeLabel: { marginTop: 2, fontSize: 12, color: '#EF4444' },
  placeLabelRight: { textAlign: 'right' },
  placeDate: { marginTop: 2, fontSize: 11, color: '#9CA3AF' },
  placeDateRight: { textAlign: 'right' },
  placeTime: { marginTop: 4, fontSize: 14, fontWeight: '600', color: '#111827' },
  placeTimeRight: { textAlign: 'right' },
  centerCol: { flex: 1, alignItems: 'center', justifyContent: 'flex-start', marginHorizontal: 0, paddingTop: 0 },
  connectorRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: 24, marginVertical: 4 },
  dashedLine: { flex: 1, borderTopWidth: 1, borderStyle: 'dashed', borderColor: '#CBD5E1', height: 1 },
  planeWrapper: { marginHorizontal: 6, alignItems: 'center', justifyContent: 'center', height: 24, width: 28 },
  planeIcon: { fontSize: 20, color: ORANGE_ACTION, lineHeight: 24 },
  centerCode: { fontSize: 12, color: '#6B7280', textAlign: 'center', fontWeight: '600', marginBottom: 2 },
  centerDuration: { fontSize: 12, color: '#6B7280', textAlign: 'center', marginTop: 2 },
  note: { marginTop: 8, fontSize: 13, color: '#6B7280' },
});

export default TransportCard;
